// @ts-nocheck

import React from 'react';
import { Box, Chip, Link, Tooltip, Typography, useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Inventory2Icon from '@mui/icons-material/Inventory2';

import ModernSection from '../ModernSection/ModernSection';

interface PricingModel {
  dbId: number;
  pricingModelType?: string;
  tierName?: string;
  tierOrdinal?: number;
  priceDisplay?: string;
  priceNumeric?: number;
  priceUnit?: string;
  priceUnitNormalized?: string;
  priceCadence?: string;
  currency?: string;
  billingPeriod?: string;
  hasFreeTier?: boolean;
  isCustomPricing?: boolean;
  keyFeatures?: string;
  confidence?: number;
}

interface CompanyProductNode {
  dbId: number;
  productName: string;
  description?: string;
  productUrl?: string;
  fullProductUrl?: string;
  productType?: string;
  confidence?: number;
  source?: string;
  sourceUrl?: string;
  fullSourceUrl?: string;
  softwareCategory?: {
    dbId: number;
    subCategory1?: string;
    subCategory2?: string;
    subCategory3?: string;
  };
  pricingModels?: PricingModel[];
}

interface ProductSectionProps {
  products: CompanyProductNode[];
  loading?: boolean;
  headerActions?: React.ReactNode;
}

const formatUnderscore = (val?: string) => (val ? val.replace(/_/g, ' ') : '');

const pricingColumns: GridColDef[] = [
  { field: 'tierName', headerName: 'Tier', width: 120 },
  {
    field: 'pricingModelType',
    headerName: 'Model',
    width: 120,
    renderCell: params =>
      params.value ? <Chip label={formatUnderscore(params.value)} size="small" variant="outlined" /> : null,
  },
  { field: 'priceNumeric', headerName: 'Amount', width: 80, type: 'number' },
  { field: 'currency', headerName: 'Curr.', width: 60 },
  {
    field: 'priceUnitNormalized',
    headerName: 'Unit',
    width: 100,
    renderCell: params => <>{formatUnderscore(params.value)}</>,
  },
  { field: 'priceCadence', headerName: 'Cadence', width: 90 },
  {
    field: 'keyFeatures',
    headerName: 'Key Features',
    width: 200,
    renderCell: params => (
      <Typography variant="body2" noWrap title={params.value}>
        {params.value}
      </Typography>
    ),
  },
  {
    field: 'hasFreeTier',
    headerName: 'Free?',
    width: 70,
    renderCell: params => (params.value ? <Chip label="Free" size="small" color="success" variant="outlined" /> : null),
  },
  {
    field: 'isCustomPricing',
    headerName: 'Custom?',
    width: 80,
    renderCell: params => (params.value ? <Chip label="Custom" size="small" color="info" variant="outlined" /> : null),
  },
];

const ProductCard = ({ product }: { product: CompanyProductNode }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const pricingRows = (product.pricingModels || [])
    .slice()
    .sort((a, b) => (a.tierOrdinal ?? 999) - (b.tierOrdinal ?? 999))
    .map(pm => ({ ...pm, id: pm.dbId }));

  return (
    <Box sx={{ mb: 2, p: 2, border: `1px solid ${isDark ? '#30363D' : 'rgba(224, 224, 224, 0.5)'}`, borderRadius: 1 }}>
      <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
        <Typography variant="subtitle1" fontWeight={600}>
          {product.productName}
        </Typography>
        {product.productType && <Chip label={product.productType} size="small" variant="outlined" />}
        {product.softwareCategory?.subCategory1 &&
          (() => {
            const parts = [
              product.softwareCategory.subCategory1,
              product.softwareCategory.subCategory2,
              product.softwareCategory.subCategory3,
            ].filter(Boolean) as string[];
            const deepest = parts[parts.length - 1];
            const breadcrumb = parts.join(' > ');
            return (
              <Tooltip title={breadcrumb} arrow>
                <Chip label={deepest} size="small" color="primary" variant="outlined" />
              </Tooltip>
            );
          })()}
        {(() => {
          const productLink = product.fullProductUrl || product.productUrl;
          const sourceLink = product.fullSourceUrl || product.sourceUrl;
          const hasBoth = productLink && sourceLink && productLink !== sourceLink;
          if (hasBoth) {
            return (
              <>
                <Link href={productLink} target="_blank" rel="noopener" variant="body2">
                  Product page
                </Link>
                <Link href={sourceLink} target="_blank" rel="noopener" variant="body2">
                  Found on
                </Link>
              </>
            );
          }
          const link = productLink || sourceLink;
          return link ? (
            <Link href={link} target="_blank" rel="noopener" variant="body2">
              Product page
            </Link>
          ) : null;
        })()}
      </Box>
      {product.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {product.description}
        </Typography>
      )}
      {pricingRows.length > 0 && (
        <DataGrid
          rows={pricingRows}
          columns={pricingColumns}
          density="compact"
          hideFooter={pricingRows.length <= 5}
          initialState={{
            pagination: { paginationModel: { pageSize: 5 } },
          }}
          pageSizeOptions={[5]}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-root': { border: 'none' },
            '& .MuiDataGrid-cell': { borderBottom: `1px solid ${isDark ? '#30363D' : 'rgba(224, 224, 224, 0.3)'}` },
          }}
        />
      )}
    </Box>
  );
};

const ProductSection: React.FC<ProductSectionProps> = ({ products, loading, headerActions }) => {
  return (
    <ModernSection
      title="Products"
      subtitle="Extracted product and pricing information"
      icon={Inventory2Icon}
      count={products?.length}
      loading={loading}
      collapsible
      defaultExpanded={products?.length > 0}
      headerActions={headerActions}
    >
      {!products || products.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No product data available
        </Typography>
      ) : (
        <Box>
          {products.map(product => (
            <ProductCard key={product.dbId} product={product} />
          ))}
        </Box>
      )}
    </ModernSection>
  );
};

export default ProductSection;
